/**
 * NX-412 — customers of other sales reps (`foreignCust.*`): the settings section in Administration, the visit labels
 * (list, board, drawer, filter) and the customer 360 visit timeline. Merged into the dictionary through `extra.ts`;
 * `foreignCustEn` is typed against `foreignCustPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const foreignCustPl = {
  reason: {
    STANDIN: 'Zastępstwo',
    CUSTOMER_REQUEST: 'Klient poprosił',
    NEARBY: 'Byłem w pobliżu',
    OTHER: 'Inny',
  },
  badge: 'u cudzego · {reason}',
  ownerLine: 'Opiekun: {owner}',
  ownerTooltip: 'Klient innego handlowca — opiekun w chwili wizyty: {owner}',
  ownerUnknown: 'nieznany',
  filter: 'U cudzych klientów',
  filterHint: 'Tylko wizyty u klientów przypisanych do innych handlowców',
  drawer: {
    title: 'Wizyta u klienta innego handlowca',
    owner: 'Opiekun klienta',
    reason: 'Powód',
    note: 'Opis',
    hint: 'Opiekun i powód zapisane w chwili rozpoczęcia wizyty. Zadanie „kolejny kontakt” z tej wizyty trafia do opiekuna.',
  },
  c360: {
    label: '{rep} · {reason}',
    note: 'Powód: {note}',
  },
  settings: {
    section: 'Praca w terenie — klienci innych handlowców',
    sectionHint: 'Czy handlowcy widzą klientów kolegów, czy mogą u nich prowadzić wizyty i kto się o tym dowiaduje.',
    loadError: 'Nie udało się wczytać ustawień pracy w terenie.',
    access: {
      title: 'Co handlowiec widzi o cudzych klientach',
      GPS_ONLY: { label: 'Nic poza GPS', hint: 'Jak dziś: tylko ostrzeżenie przy kliencie, bez listy i wyszukiwania.' },
      DIRECTORY: {
        label: 'Listę z ograniczonymi danymi',
        hint: 'Przełącznik „Wszyscy”, wyszukiwanie i lekka karta: nazwa, adres, status, opiekun, daty wizyty i zamówienia.',
      },
    },
    policy: {
      title: 'Wizyta u cudzego klienta',
      BLOCK: { label: 'Zablokowana', hint: 'Ostrzeżenie i kontakt do opiekuna, bez startu wizyty.' },
      WARN_REASON: { label: 'Dozwolona po ostrzeżeniu, z powodem', hint: 'Powód jest obowiązkowy i trafia do historii wizyty.' },
    },
    notify: {
      title: 'Kogo powiadomić',
      owner: { label: 'Opiekuna klienta', hint: 'Push i Centrum powiadomień: start i koniec wizyty.' },
      ownerManager: { label: 'Kierownika opiekuna', hint: 'Tylko Centrum powiadomień.' },
    },
    gps: {
      title: 'Ostrzeżenie GPS pokazuje',
      LIMITED: { label: 'Nazwę, adres i opiekuna', hint: 'Arkusz przy kliencie: nazwa, adres, opiekun, status, daty ostatniej wizyty i zamówienia.' },
      NONE: { label: 'Tylko komunikat', hint: '„Klient przypisany do innego handlowca” — bez nazwy, adresu i opiekuna.' },
    },
    note: {
      notifyInactive: 'Powiadomienia dotyczą wizyt — przy zablokowanej wizycie nikt ich nie dostaje.',
      gpsOnlyVisits: 'Bez listy cudzych klientów wizytę u nich da się rozpocząć tylko z ostrzeżenia GPS przy kliencie.',
      noRecipients: 'Nikt nie dostanie powiadomienia o wizycie u swojego klienta.',
    },
    unsaved: 'Niezapisane zmiany',
    reset: 'Cofnij zmiany',
    save: 'Zapisz ustawienia',
    saved: 'Zapisano ustawienia pracy w terenie',
    saveFailed: 'Nie udało się zapisać ustawień',
  },
};

export const foreignCustEn: Shape<typeof foreignCustPl> = {
  reason: {
    STANDIN: 'Standing in',
    CUSTOMER_REQUEST: 'Customer asked',
    NEARBY: 'I was nearby',
    OTHER: 'Other',
  },
  badge: "another rep's customer · {reason}",
  ownerLine: 'Account owner: {owner}',
  ownerTooltip: "Another rep's customer — account owner at the time of the visit: {owner}",
  ownerUnknown: 'unknown',
  filter: "Other reps' customers",
  filterHint: 'Only visits to customers assigned to other sales reps',
  drawer: {
    title: "Visit to another rep's customer",
    owner: 'Account owner',
    reason: 'Reason',
    note: 'Description',
    hint: 'Owner and reason are recorded when the visit starts. The "next contact" task from this visit goes to the owner.',
  },
  c360: {
    label: '{rep} · {reason}',
    note: 'Reason: {note}',
  },
  settings: {
    section: "Field work — other reps' customers",
    sectionHint: "Whether reps see their colleagues' customers, whether they may visit them and who gets told about it.",
    loadError: 'Could not load the field work settings.',
    access: {
      title: "What a rep sees about other reps' customers",
      GPS_ONLY: { label: 'Nothing but GPS', hint: 'As today: only a warning at the customer, no list and no search.' },
      DIRECTORY: {
        label: 'A list with limited data',
        hint: 'The "All" switch, search and a light card: name, address, status, owner, last visit and order dates.',
      },
    },
    policy: {
      title: "Visit to another rep's customer",
      BLOCK: { label: 'Blocked', hint: 'A warning and the owner contact, the visit cannot start.' },
      WARN_REASON: { label: 'Allowed after a warning, with a reason', hint: 'The reason is mandatory and goes to the visit history.' },
    },
    notify: {
      title: 'Who to notify',
      owner: { label: 'The account owner', hint: 'Push and Notification center: visit start and end.' },
      ownerManager: { label: "The owner's manager", hint: 'Notification center only.' },
    },
    gps: {
      title: 'The GPS warning shows',
      LIMITED: { label: 'Name, address and owner', hint: 'Sheet at the customer: name, address, owner, status, last visit and order dates.' },
      NONE: { label: 'Just a message', hint: '"Customer assigned to another sales rep" — no name, address or owner.' },
    },
    note: {
      notifyInactive: 'Notifications are about visits — while visits are blocked nobody gets them.',
      gpsOnlyVisits: "Without the list of other reps' customers a visit to them can only start from the GPS warning at the customer.",
      noRecipients: 'Nobody will be notified about a visit to their customer.',
    },
    unsaved: 'Unsaved changes',
    reset: 'Discard changes',
    save: 'Save settings',
    saved: 'Field work settings saved',
    saveFailed: 'Could not save the settings',
  },
};
